/** M1a/M1b walk result: the resolved truth the client commits. PHP persists it; it never re-prices. */
import type { ZoneEntry } from '../zones/walk-zone-crossings.js';
export interface WalkPoint { x: number; y: number; z: number }
export type PlainWalkCommitInput = Parameters<typeof preparePlainWalk>[0] & {
  encounterId: string;
  /** The scene/adapter already translated a vehicle ground-click into the operator's frame. */
  pilotDriveFramed?: true;
  /** Parent-decided pace for this drive only; ordinary speed reads keep their compiled meaning. */
  inventoryDrive?: { speed: string; speedFt: number };
  /** Advisory iframe preview offset; the parent undoes it before rebasing on its held operator/vehicle rows. */
  pilotDriveFrame?: { inventoryId: string; offset: WalkPoint };
  /** Presentation callback across the parent/scene bridge when the walk awaits external facts or consent. */
  onPending?: (message: string) => void;
  authoredGroundDrop?: boolean;
  /** The shared crossing router selected this route; downstream scopes must retain it. */
  crossingReplanned?: boolean;
  /** M5b-lite (2026-09-10, the Reach approach fold): a walk may END inside another creature's reach when the scope
   *  can PROVE no watcher reacts — the route never leaves a reach it was inside, and every watcher whose reach it
   *  enters or stays in carries no `triggers_when_target_enters_reach` / `triggers_when_target_moves` ability
   *  (leaving reach always provokes the universal Opportunity Attack and stays a legacy slice). Absent = today's
   *  rule: any proximity hands the walk to legacy. */
  reachEntry?: 'allow-proven';
  /** L1 (M3a, 2026-09-26): the scene's resolved support chain WITH footprints (the same surfaces, in the same order, as
   *  `supportChain`), so the parent can build the endpoint's chain elevation profile without re-deriving footprints. */
  supportSurfaces?: ReadonlyArray<{ id: string; z: number; footprint: { minX: number; maxX: number; minY: number; maxY: number; shape?: 'ellipse' } }>;
  /** Force Move S3: the scene's overlaid shape (live door states applied) — a player's solid-destination check reads it. */
  shape?: unknown;
  /** Wall radial intent; the parent authors its route from held inventory and live architecture. */
  wallClimb?: { id: string; direction: 'up' | 'down' | 'hold' | 'over'; holdFt?: number; sceneWall?: boolean; side?: number };
  wallClimbPlan?: import('./wall-climb-geometry.js').WallClimbPlan;
  /** Native stair intent; the parent resolves the route from held physical geometry. */
  stair?: { id: string; direction: 'up' | 'down' };
  stairPlan?: import('./stair-walk.js').StairWalkPlan;
  /** MV8 (R-008 stealth walk): the scene's awareness-pair facts with the mover at a foot (index.ts campaignStealthPairFacts). Absent = a walk
   *  near a hidden creature (or by a hidden mover) hands back as before. */
  stealthPair?: (observerId: string, subjectId: string, moverId: string, at: { x: number; y: number; z: number }) =>
    import('./stealth-walk.js').StealthPairFacts | null;
  stealthSceneReady?: () => boolean;
  /** 2026-10-07 (held-target-move.ts): a FORCE move of a creature the CASTER holds through this item's status — the caster's permission
   *  stands in for the moved row's, and the receivers prove the hold from the status row (held_target_move_authority.php). */
  heldMove?: { casterId: string; originId: string; statusIds: string[] };
};
export type TeleportClear = 'grappled' | 'restrained' | 'prone';
export interface PlainWalkResult {
  schema: 'janus.movement.m1a.v1';
  actor_id: string;
  origin: WalkPoint;
  destination: WalkPoint;
  route: Array<[number, number, number]>;
  /** M1b/M1c: the bank and the AP the walk was priced from (combat only); absent out of combat. */
  before?: { movement_pct: number; aps: number };
  /** In combat both scalars are ALWAYS present (owner Q1 2026-09-07): `movement_pct` after the walk (10 dp,
   * ft_movement_pct_store twin) and `aps` after any AP the walk banked (M1c), unchanged when the bank paid (M1b). */
  updates: { x_pos: number; y_pos: number; z_pos: number; movement_pct?: number; aps?: number };
  /** Feet of speed consumed (0 out of combat: the legacy loop does not meter there). */
  speed_cost: number;
  /** MV8 (R-008 `move-trigger`): the mover carries a triggers_on_move carrier — final_creature_move fires it ONCE at the landing (planar
   *  displacement > 0.01 ft); movement-truth.ts plans the tail (websocket/walk-move-trigger.ts) and commits it after the walk is accepted. */
  move_trigger?: true;
  /** M1d: the pool + AP could not pay the whole route, so `destination` / `updates` / `route` are the PAID PREFIX
   * (the legacy per-foot loop's stop) and the drop the player asked for is kept here (owner Q4 2026-09-07).
   * A 3D traversal also retains its requested polyline for zone entries and resisted-halt resumes. */
  truncated?: { requested: WalkPoint; paid_ft: number; message: string; route?: ReadonlyArray<readonly number[]> };
  /** Owner ruling 51: page-only notice; the movement command still carries only the resolved landing. */
  snapped?: { requested: WalkPoint; inventory: string | null; distance_ft: number; message: string };
  /** L1: present on a 3D traversal (flight, stairs, a support step, a walk through water); `route` is then the 3D polyline. */
  traversal?: { kind: 'flight' | 'walk' | 'burrow'; samples: number; difficult_steps?: number; wade_steps?: number; swim_steps?: number; climb_steps?: number };
  /** Browser-authored animation receipt, kept out of the persistence command. */
  wallClimb?: import('./wall-climb-geometry.js').WallClimbPlan;
  /** MV11: derived face checks, assuming earlier faces pass; each failure is a settled prefix. */
  climb?: { tier: 'derived'; faces: Array<{ key: string; step: number; dc: number; failure: PlainWalkResult }> };
  /** MV1 (M4b): a teleport drag. `landing` is the point asked for; `destination` is where the shared solid rule puts the
   *  body (displaced out of solid / another body), `dice` the browser's 2d10 count; `forceDamage` is its settled local notice. */
  /** MV5 (R-067): clears — the Grappled / Restrained / Prone the teleport ends (handle_teleport_status_effects; the receiver writes them). */
  teleport?: { landing: WalkPoint; displaced_ft: number; dice: number; clears?: TeleportClear[]; forceDamage?: number };
  /** MV1 (M4a): the Squeezed / Very Squeezed state the mover will rest in, present only when it differs from what the
   *  mover holds — the receiver then runs the real ft_apply_squeeze_to_creature_at_rest(). */
  squeeze?: { squeezed: boolean; very: boolean };
  /** MV1 (M3d): a burrow's end state — inside solid or standing free — and the Burrowed status transition it owes. */
  burrow?: { transition: 'enter' | 'leave' | 'none'; inside: boolean; tier: string };
  /** MV4 (MV2 ASK-1): the zone-delivered rows the legacy end-of-move exit sweeps would strip after this walk
   *  (janus-engine/zones/walk-zone-crossings.ts, `exits`). Never sent with the move: the adapter commits them through MV2's
   *  exit commit AFTER the movement commit is confirmed, and that commit re-proves every strip against SQL. */
  zone_exits?: Array<{ statusId: string; targetId: string; zoneStatusId: string; connectedId: string }>;
  /** MV2 S2 (built by MV4): the dispatching persistent-zone entries this walk makes, in foot order — committed after the move by
   *  websocket/zone-entry-truth.ts (zones/walk-zone-crossings.ts ZoneEntry). */
  zone_entries?: ZoneEntry[];
  /** Browser-decided companions saved with the operator in one CAS transaction. */
  compound?: { members: Array<{ id: string; inventoryId?: string; link: string; before: WalkPoint; after: WalkPoint }> };
  /** D-051 (owner 2026-09-30): a follower CARRIED through a persistent zone gets its OWN zone-entry effect (the legacy tail moved it with
   *  set_pos_coord and entered nothing — a front-end rule now). Per follower: the entries its carried path makes (walk-zone-crossings, the
   *  follower as the entering creature), resolved after the mover's own, at the follower's own feet. */
  follower_zone_entries?: Array<{ actorId: string; entries: NonNullable<PlainWalkResult['zone_entries']>; route: number[][];
    /** Not a follower: a creature the walk's CARRIED aura newly covers (walk-zone-crossings `covered`). It does not move — its entries
     *  resolve and commit where it stands, at its own revision. */
    at?: { x: number; y: number; z: number } }>;
  /** Janus session 3 step 4: an armed trap host fires on foot `step` of the REQUESTED walk (walk-trap.ts) — the walk is cut on the foot before. */
  trap?: { step: number; foot: [number, number]; hostId: string; abilityId: string; verb: 'magical_creature_detection' | 'step_on' | 'touch' };
  /** MV6 (R-008 `overrun`): the feet of THIS walk that force through opposing bodies (overrun-walk.ts scanOverrun — every contest assumed won);
   *  the page's planner rolls them and cuts the walk before the first loss. */
  overrun?: { triggers: Array<{ step: number; ids: string[] }> };
}

export function preparePlainWalk(input: {
  actorId: string;
  origin: WalkPoint;
  destination: WalkPoint;
  route: ReadonlyArray<readonly number[]>;
  speed: string | undefined;
  inCombat: boolean;
  force: boolean;
  supportChain: readonly unknown[];
}): PlainWalkResult | null {
  const { origin, destination, route } = input;
  if (input.inCombat || input.force || input.supportChain.length || input.speed !== 'speed_walking'
    || !input.actorId || route.length < 2 || route.length > 512
    || ![origin.x, origin.y, origin.z, destination.x, destination.y, destination.z].every(Number.isFinite)
    || origin.z !== destination.z
    || (Math.abs(origin.x - destination.x) < 0.01 && Math.abs(origin.y - destination.y) < 0.01)) return null;
  if (route.some(p => p.length < 2 || !p.every(Number.isFinite)
    || (p.length > 2 && p[2] !== origin.z))) return null;
  const first = route[0]!, last = route[route.length - 1]!;
  if (first[0] !== origin.x || first[1] !== origin.y
    || last[0] !== destination.x || last[1] !== destination.y) return null;
  return {
    schema: 'janus.movement.m1a.v1', actor_id: input.actorId,
    origin: { ...origin }, destination: { ...destination },
    route: route.map(p => [p[0]!, p[1]!, origin.z]),
    updates: { x_pos: destination.x, y_pos: destination.y, z_pos: destination.z },
    speed_cost: 0,
  };
}
